// ── TURN configuration ────────────────────────────────────────────────────────
// Priority: 1) Twilio NTS  2) REACT_APP_TURN_URL (static)  3) public fallback
//
// Simplest setup — add to .env:
//   REACT_APP_TWILIO_ACCOUNT_SID=ACxxxxxxxx
//   REACT_APP_TWILIO_AUTH_TOKEN=xxxxxxxx
//
// OR static TURN (coturn, etc.):
//   REACT_APP_TURN_URL=turn:your-server.com:3478?transport=tcp
//   REACT_APP_TURN_USERNAME=user
//   REACT_APP_TURN_CREDENTIAL=pass

const XIRSYS_IDENT           = process.env.REACT_APP_XIRSYS_IDENT;
const XIRSYS_SECRET          = process.env.REACT_APP_XIRSYS_SECRET;
const XIRSYS_CHANNEL         = process.env.REACT_APP_XIRSYS_CHANNEL || 'novas';
console.log('[NOVAS-DIAG] XIRSYS_IDENT=', XIRSYS_IDENT, 'CHANNEL=', XIRSYS_CHANNEL);
const TWILIO_SID             = process.env.REACT_APP_TWILIO_ACCOUNT_SID;
const TWILIO_TOKEN           = process.env.REACT_APP_TWILIO_AUTH_TOKEN;
const STATIC_TURN_URL        = process.env.REACT_APP_TURN_URL;
const STATIC_TURN_USERNAME   = process.env.REACT_APP_TURN_USERNAME;
const STATIC_TURN_CREDENTIAL = process.env.REACT_APP_TURN_CREDENTIAL;
const METERED_API_KEY        = process.env.REACT_APP_METERED_API_KEY;
console.log('[NOVAS-DIAG] METERED_API_KEY=', METERED_API_KEY ? `${METERED_API_KEY.slice(0, 6)}… (len ${METERED_API_KEY.length})` : METERED_API_KEY);

// Guards against a provider's endpoint being silently blocked/blackholed on
// some networks — plain fetch() has no built-in timeout and would otherwise
// hang forever, permanently stalling getIceServers() before it ever falls
// through to the next provider.
async function fetchWithTimeout(url, options = {}, timeoutMs = 6000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

// Xirsys credentials are valid 1 hour
let _xirsysServers = null;
let _xirsynsFetchedAt = 0;

async function fetchXirsysServers() {
  if (!XIRSYS_IDENT || !XIRSYS_SECRET) {
    console.warn('[WebRTC] Xirsys: env vars missing (REACT_APP_XIRSYS_IDENT / REACT_APP_XIRSYS_SECRET) — restart dev server');
    return null;
  }
  if (_xirsysServers && Date.now() - _xirsynsFetchedAt < 55 * 60 * 1000) return _xirsysServers;
  try {
    const res = await fetchWithTimeout(
      `https://global.xirsys.net/_turn/${XIRSYS_CHANNEL}`,
      { method: 'PUT', headers: { 'Authorization': `Basic ${btoa(`${XIRSYS_IDENT}:${XIRSYS_SECRET}`)}` } }
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error('[WebRTC] Xirsys API error:', res.status, text);
      return null;
    }
    const data = await res.json();
    if (data.s !== 'ok' || !data.v?.iceServers) {
      console.error('[WebRTC] Xirsys unexpected response:', data);
      return null;
    }
    _xirsysServers = data.v.iceServers
      .map(s => ({ urls: s.urls || s.url, ...(s.username ? { username: s.username } : {}), ...(s.credential ? { credential: s.credential } : {}) }))
      .filter(s => s.urls);
    _xirsynsFetchedAt = Date.now();
    return _xirsysServers;
  } catch (e) {
    console.error('[WebRTC] Xirsys fetch failed:', e.message);
    return null;
  }
}

// Twilio credentials valid 1 hour
let _twilioServers = null;
let _twilioFetchedAt = 0;

async function fetchTwilioServers() {
  if (!TWILIO_SID || !TWILIO_TOKEN) return null;
  if (_twilioServers && Date.now() - _twilioFetchedAt < 55 * 60 * 1000) return _twilioServers;
  try {
    const res = await fetchWithTimeout(
      `https://api.twilio.com/2010-04-01/Accounts/${TWILIO_SID}/Tokens.json`,
      { method: 'POST', headers: { 'Authorization': `Basic ${btoa(`${TWILIO_SID}:${TWILIO_TOKEN}`)}` } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    _twilioServers = (data.ice_servers || []).map(s => ({
      urls: s.urls || s.url,
      ...(s.username ? { username: s.username } : {}),
      ...(s.credential ? { credential: s.credential } : {}),
    }));
    _twilioFetchedAt = Date.now();
    return _twilioServers;
  } catch {
    return null;
  }
}

let _meteredServers = null;
let _meteredFetchedAt = 0;

async function fetchMeteredServers() {
  if (!METERED_API_KEY) {
    console.warn('[WebRTC] Metered.ca: REACT_APP_METERED_API_KEY not set — skipping (will fall through to Xirsys)');
    return null;
  }
  if (_meteredServers && Date.now() - _meteredFetchedAt < 12 * 3600 * 1000) return _meteredServers;
  try {
    const res = await fetchWithTimeout(
      `https://global.relay.metered.ca/api/v1/turn/credentials?apiKey=${METERED_API_KEY}`
    );
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      console.error('[WebRTC] Metered.ca API error:', res.status, body.slice(0, 200), '— check the API key in the Metered dashboard');
      return null;
    }
    _meteredServers = await res.json();
    _meteredFetchedAt = Date.now();
    return _meteredServers;
  } catch (e) {
    console.warn('[WebRTC] Metered.ca fetch failed/timed out:', e.message);
    return null;
  }
}

export async function getIceServers() {
  // Google's STUN is blocked in Russia — use Cloudflare/Twilio instead.
  const stun = [
    { urls: 'stun:stun.cloudflare.com:3478' },
    { urls: 'stun:global.stun.twilio.com:3478' },
  ];

  // 1) Metered.ca — tried first: Xirsys's free tier proved unreliable
  // (hit its bandwidth cap mid-session, and even after that reset kept
  // returning an inconsistent server count / relay-candidate availability
  // between otherwise-identical attempts).
  const metered = await fetchMeteredServers();
  if (metered) {
    console.log('[WebRTC] Using Metered.ca, ' + metered.length + ' servers: ' + JSON.stringify(metered.map(s => s.urls)));
    return metered;
  }

  // 2) Xirsys — email-only signup, free 500MB/month
  const xirsys = await fetchXirsysServers();
  if (xirsys) {
    console.log('[WebRTC] Using Xirsys, ' + xirsys.length + ' servers: ' + JSON.stringify(xirsys.map(s => s.urls)));
    return xirsys;
  }

  // 3) Twilio NTS
  const twilio = await fetchTwilioServers();
  if (twilio) {
    console.log('[WebRTC] Using Twilio NTS,', twilio.length, 'servers');
    return twilio;
  }

  // 4) Static TURN from env (coturn, etc.)
  if (STATIC_TURN_URL && STATIC_TURN_USERNAME && STATIC_TURN_CREDENTIAL) {
    const turnServers = STATIC_TURN_URL.split(',').map(u => ({
      urls: u.trim(), username: STATIC_TURN_USERNAME, credential: STATIC_TURN_CREDENTIAL,
    }));
    console.log('[WebRTC] Using static TURN,', turnServers.length, 'servers');
    return [...stun, ...turnServers];
  }

  // 5) Public fallback (dev only, unreliable on VPN)
  console.warn('[WebRTC] No TURN configured — using public fallback (unreliable on VPN)');
  return [
    ...stun,
    { urls: 'turn:openrelay.metered.ca:443?transport=tcp', username: 'openrelayproject', credential: 'openrelayproject' },
    { urls: 'turns:openrelay.metered.ca:443',              username: 'openrelayproject', credential: 'openrelayproject' },
  ];
}
